import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Upload, GripVertical } from "lucide-react";
import { API, authHeaders, imgUrl, formatApiError } from "../../lib/api";

const EMPTY = { name: "", color: "#B8912F", description: "", banner: "" };

const inputCls =
  "w-full rounded-xl border border-[#DDD8C8] bg-white px-4 py-2.5 text-sm focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20";
const labelCls = "mb-1 block text-xs font-bold uppercase tracking-wider text-neutral-500";

export default function LinesTab() {
  const [lines, setLines] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  const fileRef = useRef(null);

  const load = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/lines`),
      axios.get(`${API}/admin/products`, authHeaders()),
    ])
      .then(([l, p]) => {
        setLines(l.data);
        const c = {};
        p.data.forEach((prod) => {
          c[prod.line] = (c[prod.line] || 0) + 1;
        });
        setCounts(c);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };
  const openEdit = (l) => {
    setForm({ name: l.name, color: l.color, description: l.description || "", banner: l.banner || "" });
    setEditing(l.id);
  };

  const uploadBanner = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/upload`, fd, authHeaders());
      setForm((f) => ({ ...f, banner: data.path }));
      toast.success("Imagen subida correctamente");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setUploading(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/lines`, form, authHeaders());
        toast.success("Línea creada");
      } else {
        await axios.put(`${API}/lines/${editing}`, form, authHeaders());
        toast.success("Línea actualizada");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (l) => {
    if (!window.confirm(`¿Eliminar la línea "${l.name}"?`)) return;
    try {
      await axios.delete(`${API}/lines/${l.id}`, authHeaders());
      toast.success("Línea eliminada");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const onDrop = async (targetIndex) => {
    if (dragIndex === null || dragIndex === targetIndex) {
      setDragIndex(null);
      setOverIndex(null);
      return;
    }
    const reordered = [...lines];
    const [moved] = reordered.splice(dragIndex, 1);
    reordered.splice(targetIndex, 0, moved);
    setLines(reordered);
    setDragIndex(null);
    setOverIndex(null);
    try {
      await axios.post(`${API}/lines/reorder`, { ids: reordered.map((l) => l.id) }, authHeaders());
      toast.success("Orden de líneas actualizado");
    } catch (e) {
      toast.error(formatApiError(e));
      load();
    }
  };

  return (
    <div data-testid="lines-tab">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl font-bold text-neutral-900">Líneas de productos ({lines.length})</h2>
          <p className="text-sm text-neutral-500">
            Arrastre las líneas para cambiar el orden en la web. Una línea con productos asignados no se puede eliminar.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="line-new-button"
          className="flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-bold text-white hover:bg-brand-dark"
        >
          <Plus size={16} /> Nueva línea
        </button>
      </div>

      {loading ? (
        <p className="py-16 text-center font-mono text-xs uppercase tracking-widest text-neutral-400">Cargando…</p>
      ) : (
        <div className="grid gap-3" data-testid="admin-lines-list">
          {lines.map((l, i) => (
            <div
              key={l.id}
              draggable
              onDragStart={() => setDragIndex(i)}
              onDragOver={(e) => {
                e.preventDefault();
                setOverIndex(i);
              }}
              onDrop={() => onDrop(i)}
              onDragEnd={() => {
                setDragIndex(null);
                setOverIndex(null);
              }}
              className={`flex items-center gap-4 rounded-2xl border bg-white p-4 shadow-sm transition-all ${
                overIndex === i && dragIndex !== null && dragIndex !== i
                  ? "border-brand ring-2 ring-brand/30"
                  : "border-[#E8E4D9]"
              } ${dragIndex === i ? "opacity-50" : ""}`}
              style={{ borderLeft: `4px solid ${l.color}` }}
              data-testid={`admin-line-row-${l.id}`}
            >
              <span
                className="cursor-grab text-neutral-300 hover:text-neutral-500 active:cursor-grabbing"
                data-testid={`line-drag-${l.id}`}
                title="Arrastrar para reordenar"
              >
                <GripVertical size={18} />
              </span>
              <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-ivory">
                {l.banner ? (
                  <img src={imgUrl(l.banner)} alt={l.name} className="h-full w-full object-contain" />
                ) : (
                  <span className="h-6 w-6 rounded-full" style={{ background: l.color }} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-neutral-900">{l.name}</p>
                <p className="font-mono text-[10px] uppercase tracking-widest text-neutral-400">
                  {counts[l.id] || 0} productos · {l.color}
                </p>
              </div>
              <button
                onClick={() => openEdit(l)}
                data-testid={`line-edit-${l.id}`}
                className="flex items-center gap-1.5 rounded-full border border-neutral-200 px-4 py-2 text-xs font-bold text-neutral-600 hover:border-brand hover:text-brand"
              >
                <Pencil size={13} /> Editar
              </button>
              <button
                onClick={() => remove(l)}
                data-testid={`line-delete-${l.id}`}
                className="flex items-center gap-1.5 rounded-full border border-red-200 px-4 py-2 text-xs font-bold text-red-500 hover:bg-red-50"
              >
                <Trash2 size={13} /> Eliminar
              </button>
            </div>
          ))}
        </div>
      )}

      <AnimatePresence>
        {editing && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 backdrop-blur-sm sm:items-center sm:p-6"
            onClick={() => setEditing(null)}
            data-testid="line-form-backdrop"
          >
            <motion.form
              onSubmit={save}
              initial={{ y: 50, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 50, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-3xl bg-white p-6 sm:rounded-3xl sm:p-8"
              data-testid="line-form"
            >
              <div className="mb-6 flex items-center justify-between">
                <h3 className="font-display text-lg font-bold text-neutral-900">
                  {editing === "new" ? "Nueva línea" : "Editar línea"}
                </h3>
                <button type="button" onClick={() => setEditing(null)} data-testid="line-form-close" className="rounded-full bg-neutral-100 p-2 hover:bg-neutral-200">
                  <X size={16} />
                </button>
              </div>

              <div className="grid gap-4">
                <div>
                  <label className={labelCls}>Nombre de la línea *</label>
                  <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} placeholder="Ej: Platinum Series" data-testid="line-form-name" />
                </div>
                <div>
                  <label className={labelCls}>Color de la línea *</label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className="h-11 w-16 cursor-pointer rounded-xl border border-[#DDD8C8] bg-white p-1"
                      data-testid="line-form-color"
                    />
                    <input
                      value={form.color}
                      onChange={(e) => setForm({ ...form, color: e.target.value })}
                      className={inputCls}
                      placeholder="#B8912F"
                      data-testid="line-form-color-hex"
                    />
                  </div>
                </div>
                <div>
                  <label className={labelCls}>Descripción</label>
                  <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className={inputCls} data-testid="line-form-description" />
                </div>
                <div>
                  <label className={labelCls}>Logo / banner de la línea</label>
                  <div className="flex items-center gap-4">
                    <div className="flex h-20 w-20 items-center justify-center overflow-hidden rounded-xl border border-[#E8E4D9] bg-ivory" data-testid="line-form-banner-preview">
                      {form.banner ? (
                        <img src={imgUrl(form.banner)} alt="preview" className="h-full w-full object-contain" />
                      ) : (
                        <Upload size={18} className="text-neutral-300" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadBanner(e.target.files[0])} data-testid="line-form-file" />
                      <button
                        type="button"
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        data-testid="line-form-upload-button"
                        className="flex items-center gap-2 rounded-full border border-brand px-4 py-2 text-xs font-bold text-brand hover:bg-brand-light disabled:opacity-60"
                      >
                        <Upload size={13} /> {uploading ? "Subiendo…" : "Subir imagen"}
                      </button>
                      <input
                        value={form.banner.startsWith("http") ? form.banner : ""}
                        onChange={(e) => setForm({ ...form, banner: e.target.value })}
                        className={inputCls}
                        placeholder="…o pegue una URL de imagen"
                        data-testid="line-form-banner-url"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button
                type="submit"
                disabled={saving || uploading}
                data-testid="line-form-save"
                className="mt-6 w-full rounded-full bg-brand py-3.5 text-sm font-bold text-white hover:bg-brand-dark disabled:opacity-60"
              >
                {saving ? "Guardando…" : "Guardar línea"}
              </button>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
